import {
  mdiAccountOutline,
  mdiBus,
  mdiCalendarBlankOutline,
  mdiCalendarClockOutline,
  mdiMapMarkerOutline,
  mdiSeatPassenger,
  mdiStorefrontOutline
} from '@mdi/js';
import { Icon } from '@mdi/react';
import { Box, Divider, Tooltip, Typography } from '@mui/material';
import type { ReactNode } from 'react';

import Currency from 'src/app/components/Currency';
import { Colors } from 'src/shared/colors';

import { Props } from './DrawerDetalhesViagem';

const dividerColor = '#E5E7EB';

interface InfoItemProps {
  icon: string;
  label: string;
  truncate?: boolean;
  value: ReactNode;
  valueWeight?: 500 | 600;
}

const InfoItem = ({ icon, label, truncate = false, value, valueWeight = 600 }: InfoItemProps) => {
  const tooltip = typeof value === 'string' || typeof value === 'number' ? String(value) : '';

  return (
    <Box display="flex" alignItems="center" gap={1.75} minWidth={0}>
      <Box
        width={40}
        height={40}
        borderRadius={1.75}
        bgcolor={Colors.primary.light}
        display="flex"
        alignItems="center"
        justifyContent="center"
        flexShrink={0}
      >
        <Icon path={icon} size={0.8} color={Colors.primary.dark} />
      </Box>
      <Box minWidth={0} flex={1}>
        <Typography color={Colors.gray1} fontSize={13} fontWeight={400} lineHeight={1.25}>
          {label}
        </Typography>
        <Tooltip title={truncate ? tooltip : ''} placement="top" arrow>
          <Typography
            component="div"
            color={Colors.secondary}
            fontSize={15}
            fontWeight={valueWeight}
            lineHeight={1.4}
            mt={0.5}
            noWrap={truncate}
            sx={truncate ? undefined : { overflowWrap: 'break-word', wordBreak: 'normal' }}
          >
            {value}
          </Typography>
        </Tooltip>
      </Box>
    </Box>
  );
};

interface InfoRowProps {
  left: ReactNode;
  right?: ReactNode;
}

const InfoRow = ({ left, right }: InfoRowProps) => (
  <Box
    display="grid"
    gridTemplateColumns={{ xs: '1fr', md: right ? 'repeat(2, minmax(0, 1fr))' : '1fr' }}
    rowGap={3}
    py={2.75}
  >
    <Box minWidth={0} pr={{ md: right ? 3 : 0 }}>
      {left}
    </Box>
    {right && (
      <Box minWidth={0} pl={{ md: 3 }} borderLeft={{ xs: 0, md: `1px solid ${dividerColor}` }}>
        {right}
      </Box>
    )}
  </Box>
);

const formatarDataHora = (data: Date) => {
  const dataHora = new Date(data);

  return `${dataHora.toLocaleDateString('pt-BR')} · ${dataHora.toLocaleTimeString('pt-BR')}`;
};

export const DetalhesViagem = ({ bilhetes }: Pick<Props, 'bilhetes'>) => {
  if (!bilhetes) return null;

  const multiplosBilhetes = bilhetes.length > 1;

  return (
    <Box
      display="flex"
      flexDirection="column"
      flex={1}
      minHeight={0}
      overflow="auto"
      gap={2.5}
      px={{ xs: 2.5, sm: 3.5 }}
      pt={2}
      pb={1}
    >
      {bilhetes.map((bilhete, index) => {
        const dataVenda = bilhete.dataVenda ? (
          <InfoItem
            icon={mdiCalendarBlankOutline}
            label="Data da venda"
            value={formatarDataHora(bilhete.dataVenda)}
            valueWeight={500}
          />
        ) : null;
        const dataEmbarque = (
          <InfoItem
            icon={mdiCalendarClockOutline}
            label="Data de embarque"
            value={formatarDataHora(bilhete.dataEmbarque)}
            valueWeight={500}
          />
        );
        const pontoVenda = bilhete.pontoVenda ? (
          <InfoItem
            truncate
            icon={mdiStorefrontOutline}
            label="Ponto de venda"
            value={bilhete.pontoVenda}
            valueWeight={500}
          />
        ) : null;
        const operador = bilhete.operador ? (
          <InfoItem
            truncate
            icon={mdiAccountOutline}
            label="Operador"
            value={bilhete.operador}
            valueWeight={500}
          />
        ) : null;

        return (
          <Box
            component="section"
            key={`${bilhete.linha}-${bilhete.dataEmbarque}-${bilhete.poltrona}`}
            minWidth={0}
            border={`1px solid ${dividerColor}`}
            borderRadius={2}
            px={{ xs: 2, sm: 2.5 }}
            pb={1.5}
          >
            <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} py={1.5}>
              <Typography
                color={Colors.primary.dark}
                fontSize={13}
                fontWeight={600}
                lineHeight={1.3}
              >
                {multiplosBilhetes ? `Passagem ${index + 1}` : 'Passagem'}
              </Typography>
              <Box display="flex" alignItems="baseline" gap={0.75} flexShrink={0}>
                <Typography color={Colors.gray1} fontSize={13} fontWeight={400} lineHeight={1.3}>
                  Valor:
                </Typography>
                <Typography
                  color={Colors.primary.main}
                  fontSize={16}
                  fontWeight={700}
                  lineHeight={1.3}
                >
                  <Currency value={bilhete.preco} />
                </Typography>
              </Box>
            </Box>
            <Divider sx={{ borderColor: dividerColor }} />

            <InfoRow
              left={
                <InfoItem
                  truncate
                  icon={mdiMapMarkerOutline}
                  label="Origem"
                  value={bilhete.origem}
                />
              }
              right={
                <InfoItem
                  truncate
                  icon={mdiMapMarkerOutline}
                  label="Destino"
                  value={bilhete.destino}
                />
              }
            />
            <Divider sx={{ borderColor: dividerColor }} />

            <InfoRow
              left={dataVenda || dataEmbarque}
              right={dataVenda ? dataEmbarque : undefined}
            />
            <Divider sx={{ borderColor: dividerColor }} />

            {(pontoVenda || operador) && (
              <>
                <InfoRow left={pontoVenda || operador} right={pontoVenda ? operador : undefined} />
                <Divider sx={{ borderColor: dividerColor }} />
              </>
            )}

            <InfoRow
              left={
                <InfoItem truncate icon={mdiSeatPassenger} label="Classe" value={bilhete.classe} />
              }
              right={<InfoItem icon={mdiSeatPassenger} label="Poltrona" value={bilhete.poltrona} />}
            />
            <Divider sx={{ borderColor: dividerColor }} />

            <Box bgcolor="rgba(0, 143, 255, 0.035)" borderRadius={1.5} px={{ xs: 1.5, sm: 2 }}>
              <InfoRow
                left={<InfoItem truncate icon={mdiBus} label="Linha" value={bilhete.linha} />}
              />
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};
